import React, { useCallback, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { Panel } from '../../../../../components/atoms/Panel';
import { Folders, UploadedFile } from '../../../../../@types/uploaded-files.type';
import { useGetUploadedFiles } from '../../../../../queries/uploadedfiles-query';
import { uploadFiles } from '../../../../../api/uploadedFiles';

import { AttachmentsPanelProps } from './AttachmentsPanel.props';
import { ImageViewer } from './ImageViewer';

const FileIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 20, height: 20, flexShrink: 0 }}
    aria-hidden="true"
  >
    <path
      d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
      fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
    <polyline
      points="14 2 14 8 20 8"
      fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
    />
  </Box>
);

const ImageIcon: React.FC = () => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    sx={{ width: 20, height: 20, flexShrink: 0 }}
    aria-hidden="true"
  >
    <rect x="3" y="3" width="18" height="18" rx="2" fill="none" stroke="#6b7280" strokeWidth="1.5" />
    <circle cx="8.5" cy="8.5" r="1.5" fill="#6b7280" />
    <polyline points="21 15 16 10 5 21" fill="none" stroke="#6b7280" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </Box>
);

type FilterType = 'all' | 'images' | 'pdfs' | 'documents' | 'other';

const FILTERS: { key: FilterType; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'images', label: 'Images' },
  { key: 'pdfs', label: 'PDFs' },
  { key: 'documents', label: 'Documents' },
  { key: 'other', label: 'Other' },
];

const classifyFile = (mime: string): FilterType => {
  if (!mime) return 'other';
  if (mime.startsWith('image/')) return 'images';
  if (mime === 'application/pdf') return 'pdfs';
  if (
    mime.includes('spreadsheet') ||
    mime.includes('document') ||
    mime.includes('ms-excel') ||
    mime.includes('csv') ||
    mime.includes('text/')
  ) return 'documents';
  return 'other';
};

const getFileIcon = (mime: string) => {
  if (mime?.startsWith('image/')) return <ImageIcon />;
  return <FileIcon />;
};

export const AttachmentsPanel: React.FC<AttachmentsPanelProps> = ({
  serviceId,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [filter, setFilter] = useState<FilterType>('all');
  const [uploadError, setUploadError] = useState('');
  const [viewerFile, setViewerFile] = useState<UploadedFile | null>(null);
  const filesQuery = useGetUploadedFiles(serviceId, Folders.ServiceAttachments);
  const attachments = filesQuery.data ?? [];

  const filtered = useMemo(() => {
    if (filter === 'all') return attachments;
    return attachments.filter((f) => classifyFile(f.mimeType) === filter);
  }, [attachments, filter]);

  const counts = useMemo(() => {
    const c: Record<FilterType, number> = { all: attachments.length, images: 0, pdfs: 0, documents: 0, other: 0 };
    for (const f of attachments) {
      c[classifyFile(f.mimeType)]++;
    }
    return c;
  }, [attachments]);

  const handleUpload = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length || !serviceId) return;

    const formData = new FormData();
    for (let i = 0; i < files.length; i++) {
      formData.append('files', files[i]);
    }

    setUploadError('');
    try {
      await uploadFiles(serviceId, formData, Folders.ServiceAttachments, 'ReportedInfo');
      filesQuery.refetch();
    } catch {
      setUploadError('Upload failed — please try again');
    }

    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [serviceId, filesQuery]);

  return (
    <Panel title="Attachments">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={handleUpload}
        style={{ display: 'none' }}
        aria-label="Upload attachments"
      />

      {/* Filter chips */}
      {attachments.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.5, mb: 1, flexWrap: 'wrap' }}>
          {FILTERS.map((f) => {
            const count = counts[f.key];
            if (f.key !== 'all' && count === 0) return null;
            const active = filter === f.key;
            return (
              <Chip
                key={f.key}
                label={`${f.label}${f.key !== 'all' ? ` (${count})` : ''}`}
                size="small"
                onClick={() => setFilter(f.key)}
                sx={{
                  height: 22,
                  fontSize: '10px',
                  fontWeight: active ? 600 : 400,
                  bgcolor: active ? 'v4.accent' : 'v4.tableEven',
                  color: active ? 'common.white' : 'text.secondary',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.85 },
                }}
              />
            );
          })}
        </Box>
      )}

      {filtered.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {filtered.map((file, idx) => (
            <Box
              key={file.id ?? idx}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.75,
                px: 1,
                borderRadius: 1,
                '&:hover': { bgcolor: 'v4.tableHover' },
              }}
            >
              {getFileIcon(file.mimeType)}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Link
                  component="button"
                  underline="hover"
                  onClick={() => {
                    if (file.mimeType?.startsWith('image/')) {
                      setViewerFile(file);
                    } else {
                      window.open(file.url, '_blank', 'noopener');
                    }
                  }}
                  sx={{
                    fontSize: '13px',
                    fontWeight: 500,
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'left',
                    border: 'none',
                    background: 'none',
                    cursor: 'pointer',
                    fontFamily: 'inherit',
                  }}
                >
                  {file.fileName ?? file.key?.split('/').pop() ?? `File ${idx + 1}`}
                </Link>
              </Box>
              <Typography sx={{ fontSize: '11px', color: 'text.secondary', flexShrink: 0 }}>
                {file.mimeType ?? ''}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : (
        <Typography
          sx={{
            fontSize: '13px',
            color: 'text.secondary',
            py: 2,
            textAlign: 'center',
          }}
        >
          {filesQuery.isLoading ? (
            <CircularProgress size={16} sx={{ color: 'v4.accent' }} />
          ) : attachments.length > 0 ? (
            'No matching files'
          ) : (
            'No attachments'
          )}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
        <Button
          size="small"
          variant="contained"
          onClick={() => fileInputRef.current?.click()}
          sx={{
            fontSize: '11px',
            textTransform: 'none',
            py: 0.75,
            px: 2.5,
            bgcolor: 'v4.accent',
            '&:hover': { bgcolor: 'v4.accent', opacity: 0.9 },
          }}
        >
          Upload
        </Button>
      </Box>

      {uploadError && (
        <Typography sx={{ fontSize: '11px', color: 'error.main', mt: 0.5 }}>{uploadError}</Typography>
      )}

      {viewerFile && (
        <ImageViewer
          src={viewerFile.url}
          fileName={viewerFile.fileName ?? viewerFile.key?.split('/').pop() ?? 'Image'}
          onClose={() => setViewerFile(null)}
        />
      )}
    </Panel>
  );
};
